import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { Document } from '../types/Document';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../types/MergeTypes';

interface DocumentContextType {
    currentDocument: Document | null;
    documents: Document[];
    error: string | null;
    addDocument: (document: Document) => void;
    createDocument: (name: string) => Promise<Document>;
    updateDocument: (documentId: string, updates: Partial<Document>) => Promise<Document>;
    deleteDocument: (documentId: string) => Promise<void>;
    duplicateDocument: (documentId: string, newName: string, subcases: string[]) => Promise<Document>;
    mergeDocument: (documentId: string, mergeFromDocumentId: string, newName: string, resolveMissing: MergeMissingResolve, resolveTypeMismatch: MergeTypeMismatchResolve) => Promise<Document>;
    setCurrentDocument: (document: Document | null) => void;
    refreshDocuments: () => Promise<void>;
    fetchSubcases: (document: Document) => Promise<void>;
    /** Reload subcases from GET /documents/subcases (root category id) and update React state. */
    refreshDocumentSubcases: (documentCategoryId: string) => Promise<string[]>;
    suggestTreeFromText: (text: string, maxDepth: number, context?: string) => Promise<any>;
    expandTreeFromText: (documentId: string, text: string, maxDepth: number, context?: string) => Promise<any>;
    onOpenDocument: (document: Document) => Promise<void>;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, getToken } = useAuth();
    const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [error, setError] = useState<string | null>(null);

    const addDocument = (document: Document) => {
         setDocuments(prevDocs => [...prevDocs, document]);
    }

    const refreshDocuments = async () => {
        if (!isAuthenticated) return;
        
        try {
            setError(null);
            const response = await api.get('/documents');
            const docs = response.data;
            docs.forEach((doc: Document) => {
                doc.id = doc.name;
                doc.label = doc.name;
                fetchSubcases(doc);
            });
            setDocuments(docs);
        } catch (err) {
            setError('Failed to fetch documents');
            console.error('Error fetching documents:', err);
        }
    };

    const createDocument = async (name: string): Promise<Document> => {
        try {
            setError(null);
            const response = await api.post('/documents', { name: name });
            const newDoc = response.data;
            if ( newDoc ) {
                newDoc.id = newDoc.name;
                newDoc.label = newDoc.name;
                setDocuments(prevDocs => [...prevDocs, newDoc]);
                setCurrentDocument(newDoc);
                return newDoc;
            }
            throw new Error('Failed to create document');
        } catch (error) {
            console.error('Failed to create document:', error);
            setError('Failed to create document');
            throw error;
        } finally {
        }
    };

    const updateDocument = async (documentId: string, updates: Partial<Document>): Promise<Document> => {
        try {
            setError(null);
            const response = await api.put('/documents', updates, { params: { documentId } });
            const updatedDocument = response.data;
            if ( updatedDocument ) {
                updatedDocument.id = updatedDocument.name;
                updatedDocument.label = updatedDocument.name;
                setDocuments(prevDocs => [...prevDocs.filter(doc => doc.id !== documentId), updatedDocument]);
                if (currentDocument && currentDocument.id === documentId)
                    setCurrentDocument(updatedDocument);
                return updatedDocument;
            }
            throw new Error('Failed to update document');
        } catch (error) {
            console.error('Failed to update document:', error);
            setError('Failed to update document');
            throw error;
        } finally {
        }
    };

    const deleteDocument = async (documentId: string) => {
        try {
            setError(null);
            await api.delete('/documents', { params: { documentId } });
            await refreshDocuments();
            if (currentDocument?.id === documentId) {
                setCurrentDocument(null);
            }
        } catch (error) {
            console.error('Failed to delete document:', error);
            setError('Failed to delete document');
            throw error;
        } finally {
        }
    };

    const duplicateDocument = async (documentId: string, newName: string, subcases: string[]): Promise<Document> => {
        try {
            setError(null);
            const response = await api.post('/documents/duplicate', { documentId: documentId, newName, subcases });
            const newDoc = response.data;
            if ( newDoc ) {
                newDoc.id = newDoc.name;
                newDoc.label = newDoc.name;
                setDocuments(prevDocs => [...prevDocs, newDoc]);
                setCurrentDocument(newDoc);
                return newDoc;
            }
            throw new Error('Failed to duplicate document');
        } catch (error) {
            console.error('Failed to duplicate document:', error);
            setError('Failed to duplicate document');
            throw error;
        } finally {
        }
    };

    const mergeDocument = async (documentId: string, mergeFromDocumentId: string, newName: string, resolveMissing: MergeMissingResolve, resolveTypeMismatch: MergeTypeMismatchResolve): Promise<Document> => {
        try {
            const token = getToken();
            const response = await api.post('/documents/merge', {
                documentId: documentId,
                mergeFromDocumentId,
                newName,
                resolveMissing,
                resolveTypeMismatch
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const mergedDoc = response.data;
            if (mergedDoc) {
                if (!newName) {
                    const existingDoc = documents.find(doc => doc.id === documentId);
                    if (existingDoc) {
                        setDocuments(prevDocs => prevDocs.map(doc => 
                            doc.id === documentId ? { ...doc, ...mergedDoc } : doc
                        ));
                        setCurrentDocument(existingDoc);
                        return existingDoc;
                    }
                }
                
                mergedDoc.id = newName;
                mergedDoc.name = newName;
                mergedDoc.label = newName;
                setDocuments(prevDocs => [...prevDocs, mergedDoc]);
                setCurrentDocument(mergedDoc);
                return mergedDoc;
            }
            throw new Error('Failed to merge documents');
        } catch (error) {
            setError('Failed to merge document');
            throw error;
        }
    };

    const fetchSubcases = async (document: Document) => {
        const response = await api.get('/documents/subcases', { params: { documentId: document.categoryId } });
        document.subcases = response.data;
        document.subcases.sort();
    };

    const refreshDocumentSubcases = async (documentCategoryId: string): Promise<string[]> => {
        const response = await api.get('/documents/subcases', { params: { documentId: documentCategoryId } });
        const subcases = [...response.data].sort();
        setDocuments(prev =>
            prev.map(doc => (doc.categoryId === documentCategoryId ? { ...doc, subcases } : doc))
        );
        setCurrentDocument(prev =>
            prev && prev.categoryId === documentCategoryId ? { ...prev, subcases } : prev
        );
        return subcases;
    };

    const suggestTreeFromText = async (text: string, maxDepth: number, context?: string): Promise<any> => {
        try {
            setError(null);
            const response = await api.post('/documents/suggest-tree', { text, maxDepth, context });
            return response.data;
        } catch (error) {
            console.error('Failed to suggest tree from text:', error);
            setError('Failed to suggest tree from text');
            throw error;
        }
    };

    const expandTreeFromText = async (documentId: string, text: string, maxDepth: number, context?: string): Promise<any> => {
        try {
            setError(null);
            const response = await api.post('/documents/expand-tree', { documentId, text, maxDepth, context });
            return response.data;
        } catch (error) {
            console.error('Failed to expand tree from text:', error);
            setError('Failed to expand tree from text');
            throw error;
        }
    };



    return (
        <DocumentContext.Provider value={{
            currentDocument,
            documents,
            error,
            addDocument,
            createDocument,
            updateDocument,
            deleteDocument,
            duplicateDocument,
            mergeDocument,
            setCurrentDocument,
            refreshDocuments,
            fetchSubcases,
            refreshDocumentSubcases,
            suggestTreeFromText,
            expandTreeFromText,
            onOpenDocument: async (document: Document) => {
                setCurrentDocument(document);
            }
        }}>
            {children}
        </DocumentContext.Provider>
    );
};

export const useDocument = () => {
    const context = useContext(DocumentContext);
    if (context === undefined) {
        throw new Error('useDocument must be used within a DocumentProvider');
    }
    return context;
}; 